import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { Modal, Input, Select } from "./Leads";
import { useAuth } from "../lib/auth";
import { Plus, Pencil, Trash2, Leaf, Wrench, Factory } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  pmr: { title: "PMR Management", icon: Leaf, hint: "Track Progress Monitoring Report preparation & submissions per project.", statuses: ["Pending", "In Progress", "Submitted", "Approved"] },
  mep: { title: "MEP Coordination", icon: Wrench, hint: "Coordinate MEP design inputs, reviews and site actions.", statuses: ["Pending", "In Progress", "On Hold", "Completed"] },
  systems: { title: "STP / OWC / AQMS / ASG Systems", icon: Factory, hint: "Track environmental system supply, installation, CAMC and health.", statuses: ["Enquiry", "Proposal Sent", "Ordered", "Installation", "CAMC Active", "Completed"] },
};
const SYSTEM_TYPES = ["STP", "OWC", "AQMS", "Anti Smog Gun", "ETP", "OCEMS", "Grey Water System", "Other"];
const EMPTY = { project_id: "", title: "", system_type: "", status: "Pending", due_date: "", assigned_to: "", remarks: "" };

export default function Trackers() {
  const { kind } = useParams();
  const { user } = useAuth();
  const canEdit = user?.role === "super_admin" || !!user?.perms?.projects?.edit;
  const meta = KIND_META[kind] || KIND_META.pmr;
  const Icon = meta.icon;
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmId, setConfirmId] = useState("");

  const load = useCallback(async () => {
    setItems((await api.get(`/trackers/${kind}`)).data);
  }, [kind]);

  useEffect(() => {
    load();
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/users").then((r) => setTeam(r.data.filter((u) => u.role !== "client")));
  }, [load]);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, status: meta.statuses[0] }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/trackers/items/${editing.id}`, form);
        toast.success("Entry updated");
      } else {
        await api.post(`/trackers/${kind}`, form);
        toast.success("Entry added");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (confirmId !== id) { setConfirmId(id); setTimeout(() => setConfirmId(""), 3000); return; }
    try { await api.delete(`/trackers/items/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
    finally { setConfirmId(""); }
  };

  const teamName = (id) => team.find((t) => t.id === id)?.name || "—";
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-5 animate-fade-up" data-testid={`tracker-page-${kind}`}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold mb-1" style={{ color: "#72BE44" }}>Delivery Tracker</p>
          <h1 className="text-3xl font-bold tracking-tight text-stone-900 flex items-center gap-3">
            <Icon className="w-7 h-7" style={{ color: "#145353" }} /> {meta.title}
          </h1>
          <p className="text-sm text-stone-500 mt-1">{meta.hint}</p>
        </div>
        {canEdit && (
          <button onClick={openNew} data-testid="tracker-add-btn" className="flex items-center gap-2 text-white px-4 py-2.5 rounded-lg text-sm font-semibold" style={{ background: "#145353" }}>
            <Plus className="w-4 h-4" /> Add Entry
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {meta.statuses.slice(0, 4).map((s) => (
          <div key={s} className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="text-2xl font-bold text-stone-900">{items.filter((i) => i.status === s).length}</div>
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mt-1">{s}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="tracker-table">
          <thead className="bg-stone-50 text-left text-[10px] uppercase tracking-wider text-stone-500">
            <tr>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Project</th>
              {kind === "systems" && <th className="px-4 py-3">System</th>}
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Due Date</th>
              <th className="px-4 py-3">Assigned To</th>
              <th className="px-4 py-3">Remarks</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {items.map((it) => {
              const overdue = it.due_date && it.due_date < today && !["Completed", "Submitted", "Approved", "CAMC Active"].includes(it.status);
              return (
                <tr key={it.id} className="hover:bg-stone-50/60" data-testid={`tracker-row-${it.id}`}>
                  <td className="px-4 py-3 font-medium text-stone-900">{it.title}</td>
                  <td className="px-4 py-3 text-xs text-stone-600">{it.project_name || "—"}</td>
                  {kind === "systems" && <td className="px-4 py-3 text-xs text-stone-600">{it.system_type || "—"}</td>}
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full ${["Completed", "Submitted", "Approved", "CAMC Active"].includes(it.status) ? "bg-emerald-100 text-emerald-800" : it.status === "In Progress" ? "bg-sky-100 text-sky-800" : "bg-amber-100 text-amber-800"}`}>{it.status}</span>
                  </td>
                  <td className={`px-4 py-3 text-xs ${overdue ? "text-rose-600 font-bold" : "text-stone-600"}`}>{it.due_date || "—"}{overdue ? " ⚠" : ""}</td>
                  <td className="px-4 py-3 text-xs text-stone-600">{teamName(it.assigned_to)}</td>
                  <td className="px-4 py-3 text-xs text-stone-500 max-w-[200px] truncate" title={it.remarks}>{it.remarks || "—"}</td>
                  <td className="px-4 py-3">
                    {canEdit ? (
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openEdit(it)} data-testid={`tracker-edit-${it.id}`} className="p-1.5 rounded hover:bg-stone-100 text-stone-500"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => remove(it.id)} data-testid={`tracker-delete-${it.id}`}
                          className={`p-1.5 rounded transition ${confirmId === it.id ? "bg-rose-600 text-white text-[10px] font-bold px-2" : "text-stone-400 hover:text-rose-600 hover:bg-rose-50"}`}>
                          {confirmId === it.id ? "Confirm?" : <Trash2 className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    ) : <div className="text-center text-stone-300 text-xs">—</div>}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-stone-400"><Icon className="w-8 h-8 mx-auto mb-2 opacity-40" /> No entries yet — click "Add Entry" to start tracking</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <Modal title={editing ? "Edit Entry" : `New ${meta.title} Entry`} onClose={() => setOpen(false)}>
          <form onSubmit={submit} className="space-y-3">
            <Input label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required testid="tracker-title" />
            <Select label="Project" value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v })}
              options={[{ value: "", label: "— No project link —" }, ...projects.map((p) => ({ value: p.id, label: `${p.project_name} (${p.client_name || ""})` }))]} testid="tracker-project" />
            {kind === "systems" && (
              <Select label="System Type" value={form.system_type} onChange={(v) => setForm({ ...form, system_type: v })}
                options={SYSTEM_TYPES.map((s) => ({ value: s, label: s }))} testid="tracker-system-type" />
            )}
            <div className="grid grid-cols-2 gap-3">
              <Select label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })}
                options={meta.statuses.map((s) => ({ value: s, label: s }))} testid="tracker-status" />
              <Input label="Due Date" type="date" value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} testid="tracker-due" />
            </div>
            <Select label="Assigned To" value={form.assigned_to} onChange={(v) => setForm({ ...form, assigned_to: v })}
              options={[{ value: "", label: "— Unassigned —" }, ...team.map((t) => ({ value: t.id, label: t.name }))]} testid="tracker-assignee" />
            <Input label="Remarks" value={form.remarks} onChange={(v) => setForm({ ...form, remarks: v })} testid="tracker-remarks" />
            <button type="submit" data-testid="tracker-submit" className="w-full text-white py-2.5 rounded-lg font-semibold" style={{ background: "#72BE44" }}>
              {editing ? "Save Changes" : "Add Entry"}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
